/**
 * Greedy lane packing: the one way every calendar here stacks things that
 * overlap in time, so two overlapping blocks never hide each other (CAP-1394)
 * and a stay and a hold over the same nights read as two bars, not one.
 *
 * Extracted from the month grid's week rows when the portfolio calendar
 * (CAP-1415) needed the same answer for a property row: two copies of the
 * packing rule would drift, and the same two bookings would then stack one way
 * on the property tab and another on the portfolio.
 *
 * Positions are fractional (in days, or cells), so a stay ending part-way into
 * a day and the next one starting later that same day share a lane.
 */
export interface LaneSpan {
  /** Where the item starts. */
  left: number
  /** Where it ends; an item starting exactly here may share the lane. */
  right: number
  /**
   * Which band the item sits in. Every tier-0 item is packed first and tier 1
   * starts below all of them, so two kinds of thing stay readable as two bands
   * rather than interleaving wherever a lane happened to be free.
   */
  tier?: number
}

export interface LanePacking {
  /** The lane of each item, by its index in the input. */
  lanes: number[]
  /** How many lanes the packing used: 0 for no items. */
  laneCount: number
}

/**
 * Sorted by tier, then by start (a stable sort, so items that start together
 * keep their input order); each item takes the first lane that is free by its
 * start, or opens a new one.
 */
export function packLanes<T>(items: readonly T[], spanOf: (item: T) => LaneSpan): LanePacking {
  const spans = items.map(spanOf)
  const order = spans.map((_, index) => index)
  order.sort((a, b) => (spans[a].tier ?? 0) - (spans[b].tier ?? 0) || spans[a].left - spans[b].left)

  const lanes = new Array<number>(items.length).fill(0)
  let laneOffset = 0
  let laneCount = 0
  let currentTier: number | null = null
  let laneEnds: number[] = [] // laneEnds[lane] = right edge of the last item in it
  for (const index of order) {
    const span = spans[index]
    const tier = span.tier ?? 0
    if (currentTier === null || tier !== currentTier) {
      laneOffset = laneCount
      laneEnds = []
      currentTier = tier
    }
    let lane = laneEnds.findIndex(end => end <= span.left)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(span.right)
    } else {
      laneEnds[lane] = span.right
    }
    lanes[index] = laneOffset + lane
    laneCount = Math.max(laneCount, lanes[index] + 1)
  }
  return { lanes, laneCount }
}
